import {
  Booking,
  DisabledPrevButtonType,
  Selected,
} from '../reducers/app/appTypes';
import { SearchState } from '../reducers/search/searchTypes';
import { NO_STORE_ID } from '../services/Appointment/model';
import { isBrowser } from './browserHelper';
import {
  isClientEnteringRootPath,
  hasOperationChanged,
  getSubPathsForBoka,
  OPERATION_SUB_PAGES,
  isClientEnteringOperationPath,
} from './urlHelper';

type Locale = {
  country: string;
  language: string;
};

export type BokaSession = {
  locale: Locale;
  booking: Booking | null;
  selected: Selected;
  search: SearchState;
  from: string;
  numberOfWeeksBack: number[];
  disabledPrevButton: DisabledPrevButtonType;
  operation: string;
};

type UpdateSessionItem = (
  key: keyof BokaSession,
  value: string | Booking | Partial<Selected> | Partial<SearchState> | number[],
) => void;

const BOKA_SESSION = 'boka_session';
const DEFAULT_SESSION: BokaSession = {
  locale: {
    country: CSS_CONFIG.COUNTRY,
    language: CSS_CONFIG.LANGUAGE,
  },
  booking: null,
  selected: {
    serviceCode: '',
    serviceId: '',
    storeId: NO_STORE_ID,
    timeSlot: null,
    replacementService: {
      store: null,
      storeId: NO_STORE_ID,
      timeSlot: null,
    },
    language: null,
  },
  search: {
    postCode: '',
    city: '',
    address: '',
    state: '',
  },
  from: '',
  numberOfWeeksBack: [],
  disabledPrevButton: '',
  operation: '',
};

const hasLocaleChanged = (locale: Locale) => {
  const { COUNTRY, LANGUAGE } = CSS_CONFIG;
  return COUNTRY !== locale.country || LANGUAGE !== locale.language;
};

export const getSavedSession = (): BokaSession => {
  if (!isBrowser()) return DEFAULT_SESSION;
  const bokaSession = sessionStorage.getItem(BOKA_SESSION);
  return bokaSession
    ? { ...DEFAULT_SESSION, ...JSON.parse(bokaSession) }
    : DEFAULT_SESSION;
};

const isClientEnteringInvalidatedPath = (
  subPath: typeof SUB_PATH,
  orderNo?: string,
): boolean => {
  const hasFinalizedBooking = !!orderNo;
  const isEnteringPreBookingPath =
    isClientEnteringOperationPath('services', subPath) ||
    isClientEnteringOperationPath('timeslots', subPath) ||
    isClientEnteringOperationPath('contact', subPath);
  return hasFinalizedBooking && isEnteringPreBookingPath;
};

const isRequiredSessionDataMissing = (
  session: BokaSession,
  subPath: typeof SUB_PATH,
): boolean => {
  const { selected, booking } = session;
  const subPaths = getSubPathsForBoka(subPath);
  const operationSubPage = subPaths[1];
  const { TIME_SLOTS, CONTACT, CONFIRM } = OPERATION_SUB_PAGES;
  const selectedTimeSlot =
    selected.timeSlot || selected.replacementService?.timeSlot;
  if (operationSubPage === TIME_SLOTS) {
    return !selected.serviceId;
  }
  if (operationSubPage === CONTACT) {
    return !selectedTimeSlot || !selected.serviceId;
  }
  if (operationSubPage === CONFIRM) {
    return !booking?.orderNo || !selectedTimeSlot || !selected.serviceId;
  }
  return false;
};

const saveSession = (sessionData: BokaSession) => {
  if (!isBrowser()) return;
  sessionStorage.setItem(BOKA_SESSION, JSON.stringify(sessionData));
};

export const clearSessionData = () => {
  if (!isBrowser()) return;
  sessionStorage.removeItem(BOKA_SESSION);
};

export const getInitialSessionOnClientEntry = (
  subPath: typeof SUB_PATH,
): BokaSession => {
  const session = getSavedSession();
  if (
    isClientEnteringRootPath(subPath) ||
    isRequiredSessionDataMissing(session, subPath) ||
    isClientEnteringInvalidatedPath(subPath, session.booking?.orderNo) ||
    hasOperationChanged(session.operation, subPath) ||
    hasLocaleChanged(session.locale)
  ) {
    clearSessionData();
    return DEFAULT_SESSION;
  }

  return session;
};

export const saveUpdatedSessionItem: UpdateSessionItem = (key, value) => {
  if (!isBrowser()) return;
  const currentSession = getSavedSession();
  const isPartialObjectKey = key === 'selected' || key === 'search';
  if (isPartialObjectKey && typeof value === 'object') {
    const updatedSession = {
      ...currentSession,
      [key]: {
        ...currentSession[key],
        ...value,
      },
    };
    saveSession(updatedSession);
  } else {
    const updatedSession = {
      ...currentSession,
      [key]: value,
    };
    saveSession(updatedSession);
  }
};
